import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MdCall } from "react-icons/md";
import axios from "axios";
import { io } from "socket.io-client";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import CustomPhoneInput from "./PhoneInput"; 

// Modal Component for DND Form and Excel Upload
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md"
      >
        <h2 className="text-xl font-semibold mb-4">{title}</h2>
        {children}
        <button
          onClick={onClose}
          className="mt-4 px-4 py-2 bg-gray-300 text-black rounded-lg hover:bg-gray-400 w-full"
        >
          Close
        </button>
      </motion.div>
    </div>
  );
};


const DoNotDisturb = ({user}) => {
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dndData, setDndData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); 
  const recordsPerPage = 5; 
const [formState, setFormState] = useState({
    name: "",
    to: "",
    location: "",
    email: "",
    reason: "",
  });
  const [isDndModalOpen, setIsDndModalOpen] = useState(false);
  const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
  // Fetch DnD list from API
  const fetchDndList = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${process.env.REACT_APP_SERVER_API}/api/dnd-list?userId=${user.id}&role=${user.role}`);
      setDndData(response.data);
      console.log("response " ,response.data)
      
    } catch (err) {
      setError("Failed to fetch DnD list. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial data and set up WebSocket listener
  useEffect(() => {
    fetchDndList();

    const socket = io(process.env.REACT_APP_SERVER_API);
    socket.on("dndUpdate", () => {
      fetchDndList(); // Refresh when a new DND entry is added
    });

    return () => socket.disconnect();
  }, [user]);

//   // Filter data based on search
//   const filteredData = dndData.filter((row) =>
//   typeof row.from === 'string' &&
//   row.from.toLowerCase().includes(search.toLowerCase())
// );

const filteredData = dndData.filter((row) =>
  typeof row.to === 'string' &&
  row.to.toLowerCase().includes(search.toLowerCase())
);

  // --- Updated function to handle manual DND addition ---
  const handleAddToDnd = async (e) => {
    e.preventDefault();
    const { name, to, email, location, reason } = formState;
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_SERVER_API}/api/add-dnd-list`, {
        userId: user.id,
        name,
        to,
        location: location || "Unknown",
        email,
        reason: reason || "Manually added via UI",
      });
      toast.success(response.data.message);
      setFormState({ name: "", to: "", location: "", email: "", reason: "" });
      setIsDndModalOpen(false);
      fetchDndList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error adding to DND list");
    }
  };

  // --- Updated function to handle Excel upload ---
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      toast.error("Please select an Excel file.");
      return;
    }
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);

        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        for (const row of rows) {
          const { Name, to, Location, Email, Reason } = row;
          if (!Name || !to || !Email) {
            errorCount++;
            errors.push(`Row ${successCount + errorCount}: Missing Name, to, or Email`);
            continue;
          }
          try {
            await axios.post(`${process.env.REACT_APP_SERVER_API}/api/add-dnd-list`, {
              userId: user.id,
              name: Name,
              to: to,
              location: Location || "Unknown",
              email: Email,
              reason: Reason || "Added via Excel upload",
            });
            successCount++;
          } catch (err) {
            errorCount++;
            errors.push(
              `Row ${successCount + errorCount}: ${err.response?.data?.message || "Error adding entry"}`
            );
          }
        }

        if (successCount > 0) {
          toast.success(`Processed ${rows.length} rows: ${successCount} added, ${errorCount} failed.`);
        }
        if (errorCount > 0) {
          toast.error(`Errors: ${errors.join("; ")}`);
        }
        fetchDndList();
        setIsExcelModalOpen(false);
        e.target.value = null;
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      toast.error("Error processing Excel file");
      console.error(err);
    }
  };




  // Pagination logic
  const totalRecords = filteredData.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const exportToCSV = () => {
    const csvContent = [
      ["ID", "From", "To", "Start Time", "End Time", "Sentiment Status"],
      ...filteredData.map((row, index) => [
        index + 1, // Continuous ID across all records
        row.from,
        row.to,
        row.startTime,
        row.endTime,
        row.agentStatus,
      ]),
    ]
      .map((e) => e.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "dnd_list.csv";
    link.click();
  };

  const exportToXLSX = () => {
    console.log("Export to XLSX functionality requires 'xlsx' library");
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="p-6 bg-gray-50">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="flex justify-between items-center mb-6"
      >
        <h1 className="text-3xl font-bold text-gray-900">Do Not Disturb</h1>
        
      </motion.div>


      {/* --- Updated Search Bar and Buttons Section --- */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-6"
      >
        <div className="flex items-center justify-between w-full space-x-4">
          <input
            type="text"
            placeholder="Search by Name"
            className="w-full md:w-1/4 px-4 py-3 border border-gray-300 rounded-xl shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 ease-in-out transform focus:scale-105"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="flex space-x-4">
            {/* --- Added Add DND Button --- */}
            <button
              className="px-6 py-2 text-black rounded-lg border border-black shadow-md hover:bg-gray-100 focus:outline-none"
              onClick={() => setIsDndModalOpen(true)}
            >
              Add DND
            </button>
            {/* --- Added Import Excel Button --- */}
            <button
              className="px-6 py-2 text-black rounded-lg border border-black shadow-md hover:bg-gray-100 focus:outline-none"
              onClick={() => setIsExcelModalOpen(true)}
            >
              Import Excel
            </button>
            <button
              className="px-6 py-2 text-black rounded-lg border border-black shadow-md hover:bg-gray-100 focus:outline-none"
              onClick={exportToCSV}
            >
              Export to CSV
            </button>
            <button
              className="px-6 py-2 text-black rounded-lg border border-black shadow-md hover:bg-gray-100 focus:outline-none"
              onClick={exportToXLSX}
            >
              Export to XLSX
            </button>
          </div>
        </div>
      </motion.div>

      {/* --- Added DND Form Modal --- */}
      <Modal
        isOpen={isDndModalOpen}
        onClose={() => setIsDndModalOpen(false)}
        title="Add DND Customer"
      >
        <form onSubmit={handleAddToDnd} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              placeholder="Enter name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formState.name}
              onChange={(e) => setFormState({ ...formState, name: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Phone</label>
           <CustomPhoneInput
              value={formState.to}
              onChange={(to) => setFormState({ ...formState, to })}
              onBlur={() => {
                if (!formState.to) {
                  setFormState({ ...formState, to: "+1" });
                }
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Location (Optional)</label>
            <input
              type="text"
              placeholder="Enter location"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formState.location}
              onChange={(e) => setFormState({ ...formState, location: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              placeholder="Enter email"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formState.email}
              onChange={(e) => setFormState({ ...formState, email: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Reason (Optional)</label>
            <input
              type="text"
              placeholder="Enter reason"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formState.reason}
              onChange={(e) => setFormState({ ...formState, reason: e.target.value })}
            />
          </div>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Add to DND
          </button>
        </form>
      </Modal>

      {/* --- Added Excel Import Modal --- */}
      <Modal
        isOpen={isExcelModalOpen}
        onClose={() => setIsExcelModalOpen(false)}
        title="Import DND from Excel"
      >
        <div className="flex flex-col gap-4">
          <label className="block text-sm font-medium text-gray-700">
            Upload Excel File (Columns: Name, to, Location, Email, Reason)
          </label>
          <input
            type="file"
            accept=".xlsx, .xls"
            onChange={handleExcelUpload}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          />
        </div>
      </Modal>



      {/* Loading and Error States */}
      {loading && (
        <motion.div
          className="flex justify-center items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="animate-spin w-8 h-8 border-4 border-t-4 border-blue-500 rounded-full"></div>
        </motion.div>
      )}
      {error && (
        <motion.p
          className="text-red-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {error}
        </motion.p>
      )}

      {/* Dynamic Table */}
      {!loading && !error && (
        <>
          <motion.div
            className="overflow-x-auto bg-white shadow-lg rounded-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-md">
              <thead className="sticky top-0 bg-blue-400 text-white">
                <tr>
                  <th className="px-6 py-3 text-left">Sr</th>
                  <th className="px-6 py-3 text-left">From</th>
                  <th className="px-6 py-3 text-left">MobileNumber</th>
                  <th className="px-6 py-3 text-left">Start Time</th>
                  <th className="px-6 py-3 text-left">End Time</th>
                  <th className="px-6 py-3 text-left">Sentiment Status</th>
                  <th className="px-6 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((row, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-6 py-4">{startIndex + index + 1}</td> {/* Continuous ID */}
                      <td className="px-6 py-4">{row.from}</td>
                      <td className="px-6 py-4">{row.to}</td>
                  <td className="px-6 py-4">
  {new Date(row.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
</td>
<td className="px-6 py-4">
  {new Date(row.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
</td>
                      <td className="px-6 py-4">{row.agentStatus}</td>
                      <td className="px-6 py-4">
                        <button className="text-blue-500 hover:text-blue-700">
                          <MdCall size={24} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center">
                      No DnD records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </motion.div>

          {/* Pagination Controls */}
          {totalRecords > 0 && (
            <div className="flex justify-center mt-4 space-x-2">
              <button
                className={`px-4 py-2 rounded-lg ${currentPage === 1 ? "bg-gray-300" : "bg-blue-500 text-white hover:bg-blue-600"}`}
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`px-4 py-2 rounded-lg ${currentPage === page ? "bg-blue-700 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className={`px-4 py-2 rounded-lg ${currentPage === totalPages ? "bg-gray-300" : "bg-blue-500 text-white hover:bg-blue-600"}`}
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DoNotDisturb;